import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AuthLayout from '../../components/layout/AuthLayout';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import { Calendar } from 'primereact/calendar';
import {
    useAddProjectMutation,
    useEditProjectMutation,
    useGetProjectQuery,
    useGetProjectStatusesQuery
} from '../../api/projects.js';
import { useGetEconomicRegionsQuery, useGetSkillsQuery } from '../../api/misc.js';
import { PERMISSION_FILES, PERMISSION_PROJECTS } from '../../utils/constants.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import { useSelector } from 'react-redux';
import { Accordion, AccordionTab } from 'primereact/accordion';
import ProjectCompanies from '../../components/misc/projects/ProjectCompanies.jsx';
import ProjectMilestoneReport from '../../components/misc/projects/ProjectMilestoneReport.jsx';
import ProjectContacts from '../../components/misc/projects/ProjectContacts.jsx';
import ProjectDeleteConfirmation from '../../components/misc/projects/ProjectDeleteConfirmation.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import BaseCard from '../../components/misc/BaseCard.jsx';
import IconClose from '../../components/images/IconClose.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import BaseTreeSelect from '../../components/form/BaseTreeSelect.jsx';
import { PROJECT_SCHEMA } from '../../utils/project.js';
import { addTreeSelection, findItem, removeAttribute } from '../../utils/helper.js';
import { Badge } from 'primereact/badge';
import BaseSwitch from '../../components/form/BaseSwitch.jsx';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import AssociateFiles from '../../components/misc/AssociateFiles.jsx';
import { transformFilesForRequest } from '../../utils/file.js';

const AddEditProject = ({ isEditMode = false }) => {
    let project = null;
    const { id } = useParams();
    const navigate = useNavigate();
    const { permissions: userPermissions } = useCurrentUser();
    const user = useSelector((state) => state.user.user);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [isPrivate, setIsPrivate] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [milestonesState, setMilestonesState] = useState([]);
    const [reportsState, setReportsState] = useState([]);
    const [contacts, setContacts] = useState([]);

    const [showReachOverlay, setShowReachOverlay] = useState(false);
    const [needsOrCapabilities, setNeedsOrCapabilities] = useState('');
    const [selectedCapabilities, setSelectedCapabilities] = useState('');
    const [selectedNeeds, setSelectedNeeds] = useState('');
    const [showOverlay, setShowOverlay] = useState(false);
    const [initialReach, setInitialReach] = useState([]);
    const [selectedEconomicRegions, setSelectedEconomicRegions] = useState([]);

    const [showFileSearch, setShowFileSearch] = useState(true);

    if (isEditMode && id) {
        const { data: projectResponse } = useGetProjectQuery(id);

        project = useMemo(() => {
            if (projectResponse?.response?.project) {
                return projectResponse.response.project;
            }

            return null;
        }, [projectResponse]);
    }

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;

        user.organizations.forEach((item) => {
            const permission = user.role.permissions.find(
                (value) => value.organization_id === item.id && value.permission === PERMISSION_PROJECTS
            );

            const hasPermission =
                permission && ((permission.create && !isEditMode) || (permission.update && isEditMode));

            if (hasPermission) {
                setPermittedOrganizations((prevState) => {
                    if (!prevState.some((org) => org.id === item.id)) {
                        return [...prevState, item];
                    }
                    return prevState;
                });
            }
        });
    }, [user, isEditMode]);

    const { data: statuses, isLoading: statusesLoading } = useGetProjectStatusesQuery();

    const statusesList = Array.isArray(statuses?.response?.project_statuses)
        ? statuses.response.project_statuses.map((status) => ({
              label: status.label,
              value: status.value
          }))
        : [];

    const [addProject, { isLoading: isAdding }] = useAddProjectMutation();
    const [editProject, { isLoading: isEditing }] = useEditProjectMutation();

    const treeElement = useCallback((key, item) => {
        return {
            key,
            label: item.name,
            data: item.id,
            ...(item.sub_skills && item.sub_skills.length > 0
                ? {
                      children: item.sub_skills.map((sub_capability, index) => {
                          return treeElement(`${key}-${index}`, sub_capability);
                      })
                  }
                : {})
        };
    }, []);

    const categoryMultiselect = (item) => {
        return {
            label: item.name,
            ...(item.countries && item.countries.length > 0
                ? {
                      items: item.countries.map((value) => {
                          return {
                              label: value.name,
                              value: value.id
                          };
                      })
                  }
                : {})
        };
    };

    const { data: skillsData } = useGetSkillsQuery({});
    const skills = useMemo(() => {
        let skillOptions = [];
        if (skillsData?.response?.skills) {
            let skillsArr = skillsData.response.skills;
            skillsArr.map((item, index) => {
                skillOptions.push(treeElement(index, item));
            });
        }

        return skillOptions;
    }, [skillsData, treeElement]);

    const { data: economicRegionsData } = useGetEconomicRegionsQuery({});
    const economicRegions = useMemo(() => {
        const economicRegionsOptions = [];
        if (economicRegionsData?.response?.economic_regions) {
            economicRegionsData.response.economic_regions.map((region) => {
                economicRegionsOptions.push(categoryMultiselect(region));
            });
        }
        return economicRegionsOptions;
    }, [economicRegionsData]);

    const {
        handleSubmit,
        setValue,
        control,
        clearErrors,
        setError,
        watch,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(PROJECT_SCHEMA),
        defaultValues: {
            name: '',
            description: '',
            status: 'planned',
            budget: '',
            actual_costs: '',
            start_date: null,
            end_date: null,
            shared: false,
            shared_organizations: [],
            shared_users: [],
            needs: [],
            capabilities: [],
            files: [],
            reach: [],
            international: false
        }
    });

    const international = watch('international');

    const {
        fields: fieldsReach,
        append: appendReach,
        remove: removeReach
    } = useFieldArray({
        control,
        name: 'reach'
    });

    const {
        fields: fieldsNeeds,
        append: appendNeed,
        remove: removeNeed
    } = useFieldArray({
        control,
        name: 'needs'
    });

    const {
        fields: fieldsCapabilities,
        append: appendCapability,
        remove: removeCapability
    } = useFieldArray({
        control,
        name: 'capabilities'
    });

    const {
        fields: fieldsFiles,
        append: appendFile,
        remove: removeFile
    } = useFieldArray({
        control,
        name: 'files'
    });

    const closeNeedsAndCapabilities = () => {
        setShowOverlay(false);
        setNeedsOrCapabilities('');
    };

    const addNeedsOrCapabilities = (mode) => {
        setShowOverlay(true);
        setNeedsOrCapabilities(mode);
    };

    const selectReach = useCallback(
        (value) => {
            if (
                economicRegions &&
                economicRegions.every((region) => region.items?.every((item) => value.includes(item.value)))
            ) {
                setValue('international', true);
            } else {
                value.forEach((item) => {
                    if (!fieldsReach.some((field) => field.economic_region_id === item)) {
                        const foundItem = economicRegions
                            .flatMap((region) => region.items)
                            .find((element) => element.value === item);

                        const itemLabel = foundItem ? foundItem.label : null;
                        appendReach({
                            name: itemLabel,
                            economic_region_id: item
                        });
                    }
                });

                const indicesToRemove = [];
                fieldsReach.forEach((item, index) => {
                    if (!value.includes(item.economic_region_id)) {
                        indicesToRemove.push(index);
                    }
                });

                indicesToRemove.reverse().forEach((index) => removeReach(index));
            }
            setSelectedEconomicRegions(value);
        },
        [appendReach, economicRegions, fieldsReach, removeReach, setValue]
    );

    const setAttribute = (treeArray, field, func, property) => {
        let arr = {};

        const processNode = (node, selectedIds) => {
            let key = findItem(node, node.data);
            if (key === undefined || key === null) return { checked: false, partialChecked: false };

            if (node.children && node.children.length > 0) {
                let childrenStates = node.children.map((child) => processNode(child, selectedIds));

                let allChildrenChecked = childrenStates.every((child) => child.checked);
                let someChildrenChecked = childrenStates.some((child) => child.checked || child.partialChecked);

                arr[key] = {
                    checked: allChildrenChecked,
                    partialChecked: someChildrenChecked && !allChildrenChecked
                };
                return arr[key];
            } else {
                let isSelected = selectedIds.includes(node.data);
                arr[key] = { checked: isSelected, partialChecked: false };
                return arr[key];
            }
        };

        let selectedIds = field.map((item) => item[property]);

        treeArray.forEach((capability) => {
            processNode(capability, selectedIds);
        });

        func(arr);
    };

    const handleDeleteConfirmation = () => {
        setDeleteVisible(true);
    };

    useEffect(() => {
        if (isEditMode && project) {
            if (!canUpdateOrDelete(user, project, PERMISSION_PROJECTS, 'update')) {
                navigate('/unauthorized');
                return;
            }
            const prefillData = {
                ...project,
                shared_users: project.shared_users?.map((user) => user.id) || [],
                shared_organizations: project.shared_organizations,
                shared: project.shared || false,
                start_date: project.start_date ? new Date(project.start_date) : null,
                end_date: project.end_date ? new Date(project.end_date) : null,
                needs: project.needs || [],
                capabilities: project.capabilities || [],
                files:
                    project.files?.map((file) => ({
                        file_id: file.file_id,
                        name: file.title,
                        path: file.url,
                        extension: file.extension,
                        type: file.category
                    })) || [],
                international: project.international,
                ...(!project.international ? { reach: project.reach } : {})
            };

            setSelectedEconomicRegions(project.reach.map((item) => item.economic_region_id));
            setInitialReach(project.reach.map((item) => item.economic_region_id));
            setIsPrivate(!project.shared);

            Object.entries(prefillData).forEach(([key, value]) => {
                setValue(key, value !== null && value !== undefined ? value : '');
            });

            setCompanies(
                project.companies?.map((company) => ({
                    organization_id: company.organization_id,
                    type: company.type,
                    name: company.name
                })) || []
            );

            setContacts(
                project.contacts?.map((contact) => ({
                    contact_id: contact.contact_id,
                    first_name: contact.first_name,
                    last_name: contact.last_name,
                    email: contact.email,
                    role: contact.role,
                    is_lead: contact.is_lead
                })) || []
            );

            if (project.status) {
                setValue('status', project.status);
            }
        }
    }, [isEditMode, navigate, project, setValue, user]);

    const sharedOrganizations = watch('shared_organizations');
    const sharedUsers = watch('shared_users');

    const handleSharedOrganizations = (index, value) => {
        if (permittedOrganizations.length > 0) {
            const organizationId = permittedOrganizations[index].id;

            const updatedSharedOrganizations = value
                ? [...sharedOrganizations, organizationId]
                : sharedOrganizations.filter((orgId) => orgId !== organizationId);

            setValue('shared_organizations', updatedSharedOrganizations);

            if (updatedSharedOrganizations.length === 0) {
                handleSharedUsers(true);
            }
        }
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length > 0) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const updatedSharedUsers = value ? [...sharedUsers, id] : sharedUsers.filter((userId) => userId !== id);

        setValue('shared_users', updatedSharedUsers);
    };

    const renderShared = () => {
        if (permittedOrganizations.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }

        return (
            <div>
                {permittedOrganizations.map(({ id, name }, index) => (
                    <SharedModelListItem
                        key={id}
                        index={index}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        isLoading={isAdding}
                        disabled={isPrivate}
                    />
                ))}

                <PrivateUsersListItem
                    key='separate-item'
                    text={'Private'}
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={project?.shared_users || []}
                    isLoading={isAdding}
                    permission={PERMISSION_PROJECTS}
                />
            </div>
        );
    };

    const onSubmit = async (data) => {
        const finalFiles = transformFilesForRequest(data.files);

        const payload = {
            shared: data.shared_organizations.length > 0,
            shared_organizations: sharedOrganizations,
            shared_users: sharedUsers,
            name: data.name,
            description: data.description,
            status: data.status,
            budget: parseFloat(data.budget) || 0,
            actual_costs: parseFloat(data.actual_costs) || 0,
            start_date: data.start_date,
            end_date: data.end_date,
            milestones: milestonesState,
            reports: reportsState,
            companies,
            needs: data.needs,
            capabilities: data.capabilities,
            files: finalFiles,
            contacts: contacts.map((contact) => ({
                contact_id: contact.contact_id,
                first_name: contact.first_name,
                last_name: contact.last_name,
                email: contact.email,
                role: contact.role,
                is_lead: contact.is_lead
            })),
            international: data.international,
            ...(!data.international ? { reach: data.reach } : {})
        };

        try {
            if (isEditMode) {
                await editProject({ id, data: payload }).unwrap();
                navigate(`/projects/${id}`);
            } else {
                await addProject(payload).then((apiResponse) => {
                    if (apiResponse.data?.success) {
                        navigate(`/projects/${apiResponse.data?.response.id}`);
                    }
                });
            }
        } catch (error) {
            console.error('Submission failed', error);
            if (error?.data?.errors) {
                Object.entries(error.data.errors).forEach(([field, messages]) => {
                    setError(field, { type: 'server', message: messages[0] });
                });
            }
        }
    };

    useEffect(() => {
        setAttribute(skills, fieldsCapabilities, setSelectedCapabilities, 'capability_id');
        setAttribute(skills, fieldsNeeds, setSelectedNeeds, 'need_id');
    }, [skills, fieldsCapabilities, fieldsNeeds]);

    useEffect(() => {
        if (international) {
            selectReach(initialReach);
            if (showReachOverlay) {
                setShowReachOverlay(false);
            }
        }
    }, [international, initialReach, selectReach, showReachOverlay]);

    const groupHeaderTemplate = (option) => {
        const allSelected = option.items.every((child) => selectedEconomicRegions.includes(child.value));

        const onGroupCheckboxChange = (checked) => {
            let newValues = [...selectedEconomicRegions];
            if (checked) {
                option.items.forEach((child) => {
                    if (!newValues.includes(child.value)) {
                        newValues.push(child.value);
                    }
                });
            } else {
                newValues = newValues.filter((val) => !option.items.some((child) => child.value === val));
            }
            selectReach(newValues);
        };

        const onRowClick = () => {
            onGroupCheckboxChange(!allSelected);
        };

        return (
            <div className={'p-multiselect-item px-0 py-0 cursor-pointer'} onClick={onRowClick}>
                <Checkbox
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onGroupCheckboxChange(e.checked)}
                    className='gap-2'
                />
                <b>{option.label}</b>
            </div>
        );
    };

    const renderReach = () => (
        <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-title2'>Reach</div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='reach-tab'
                            className={`custom-accordion-tab-no-padding`}
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Reach</span>
                                    <Badge
                                        value={international ? 'International' : fieldsReach.length}
                                        className='ns-badge'
                                    ></Badge>
                                </>
                            }
                        >
                            <div className={'mb-4'}>
                                <BaseSwitch
                                    control={control}
                                    name='international'
                                    label={'International'}
                                    clearErrors={clearErrors}
                                    errors={[]}
                                />
                            </div>
                            <div className='flex items-center flex-wrap'>
                                {!international &&
                                    fieldsReach.length > 0 &&
                                    fieldsReach.map((region, index) => (
                                        <span
                                            key={region.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{region.name}</span>
                                            <span
                                                onClick={() => {
                                                    selectReach(
                                                        fieldsReach
                                                            .filter(
                                                                (item) =>
                                                                    item.economic_region_id !==
                                                                    region.economic_region_id
                                                            )
                                                            .map((item) => item.economic_region_id)
                                                    );
                                                }}
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                {!international && (
                                    <span
                                        onClick={() => setShowReachOverlay(true)}
                                        className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                    >
                                        <span>Add Reach</span>
                                        <span>
                                            <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                        </span>
                                    </span>
                                )}
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
        </BaseCard>
    );

    const renderNeedsAndCapabilities = () => (
        <BaseCard className='border-0 bg-theme1-tan p-0 pb-6'>
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-title2'>Needs &amp; Capabilities</div>
            </div>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='needs-tab'
                            className='custom-accordion-tab-no-padding'
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Needs</span>
                                    <Badge value={fieldsNeeds?.length} className='ns-badge'></Badge>
                                </>
                            }
                        >
                            <div className='flex items-center flex-wrap'>
                                {fieldsNeeds.length > 0 &&
                                    fieldsNeeds.map((need, index) => (
                                        <span
                                            key={need.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{need.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skills,
                                                        index,
                                                        need.need_id,
                                                        setSelectedNeeds,
                                                        'need_id',
                                                        fieldsNeeds,
                                                        removeNeed
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                <span
                                    onClick={() => addNeedsOrCapabilities('needs')}
                                    className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                >
                                    <span>Add Need</span>
                                    <span>
                                        <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                    </span>
                                </span>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
            <BaseCard className='border-0 m-0 mb-6 p-4 shadow-none mx-6 w-auto px-2'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <Accordion className='custom-accordion' activeIndex={isEditMode ? null : 0}>
                        <AccordionTab
                            key='capabilities-tab'
                            className='custom-accordion-tab-no-padding'
                            header={
                                <>
                                    <span className='text-lg font-bold text-theme1-primary'>Capabilities</span>
                                    <Badge value={fieldsCapabilities?.length} className='ns-badge'></Badge>
                                </>
                            }
                        >
                            <div className='flex items-center flex-wrap'>
                                {fieldsCapabilities.length > 0 &&
                                    fieldsCapabilities.map((capability, index) => (
                                        <span
                                            key={capability.id}
                                            className='mr-2 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center w-fit'
                                        >
                                            <span>{capability.name}</span>
                                            <span
                                                onClick={() =>
                                                    removeAttribute(
                                                        skills,
                                                        index,
                                                        capability.capability_id,
                                                        setSelectedCapabilities,
                                                        'capability_id',
                                                        fieldsCapabilities,
                                                        removeCapability
                                                    )
                                                }
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                <span
                                    onClick={() => addNeedsOrCapabilities('capabilities')}
                                    className='mr-4 mb-4 px-4 py-2 border-2 border-dashed border-theme1-gold1 rounded-4xl text-theme1-gold1 font-bold flex items-center w-fit'
                                >
                                    <span>Add Capability</span>
                                    <span>
                                        <IconAdd className='w-6 h-6 fill-theme1-gold1' />
                                    </span>
                                </span>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            </BaseCard>
        </BaseCard>
    );

    return (
        <AuthLayout
            title={isEditMode ? 'Edit Project' : 'Add Project'}
            backRoute={isEditMode ? `/projects/${id}` : '/projects'}
        >
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='mb-4'>
                        <BaseInput
                            id='name'
                            name='name'
                            label='Name'
                            type='text'
                            errors={errors.name ? [errors.name.message] : []}
                            control={control}
                        />
                    </div>
                    <div className='mb-4'>
                        <BaseInput
                            id='description'
                            name='description'
                            label='Description'
                            type='text'
                            errors={errors.description ? [errors.description.message] : []}
                            control={control}
                        />
                    </div>

                    <div className='mb-4'>
                        <ProjectCompanies isEditMode={isEditMode} companies={companies} setCompanies={setCompanies} />
                    </div>

                    <div className='mb-4'>
                        <label className='block text-theme1-primary2 font-bold text-base'>Start Date</label>
                        <Calendar
                            id='start_date'
                            value={watch('start_date')}
                            onChange={(e) => setValue('start_date', e.value)}
                            placeholder='Select Start Date'
                            className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 text-base'
                            inputClassName='custom-calendar-input'
                            dateFormat='dd/mm/yy'
                        />
                        {errors.start_date && <p className='text-red-500 text-sm'>{errors.start_date.message}</p>}
                    </div>

                    <div className='mb-4'>
                        <label className='block text-theme1-primary2 font-bold text-base'>End Date</label>
                        <Calendar
                            id='end_date'
                            value={watch('end_date')}
                            onChange={(e) => setValue('end_date', e.value)}
                            placeholder='Select End Date'
                            className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 text-base'
                            inputClassName='custom-calendar-input'
                            dateFormat='dd/mm/yy'
                            minDate={watch('start_date')}
                        />
                        {errors.end_date && <p className='text-red-500 text-sm'>{errors.end_date.message}</p>}
                    </div>

                    <div className='mb-4'>
                        <BaseDropdown
                            id='status'
                            name='status'
                            label='Status'
                            placeholder='Select status'
                            errors={errors.status ? [errors.status.message] : []}
                            control={control}
                            options={statusesList}
                            disabled={statusesLoading}
                            filter={false}
                        />
                    </div>

                    <div className='mb-4'>
                        <BaseInput
                            id='budget'
                            name='budget'
                            label='Budget'
                            type='text'
                            errors={errors.budget ? [errors.budget.message] : []}
                            control={control}
                        />
                    </div>

                    <div className='mb-4'>
                        <BaseInput
                            id='actual_costs'
                            name='actual_costs'
                            label='Actual Costs'
                            type='text'
                            errors={errors.actual_costs ? [errors.actual_costs.message] : []}
                            control={control}
                        />
                    </div>

                    <Accordion
                        className={`custom-accordion mt-2 ${!isEditMode ? 'mb-8' : 'pb-6'}`}
                        multiple={false}
                        activeIndex={isEditMode ? null : 0}
                    >
                        <AccordionTab
                            key='shared-within'
                            header={
                                <span className='text-lg font-bold text-theme1-text2'>
                                    Shared within {!isEditMode && <span className='text-theme1-brightRed'>*</span>}
                                </span>
                            }
                        >
                            {renderShared()}
                        </AccordionTab>
                    </Accordion>

                    <ProjectMilestoneReport
                        type='Milestones'
                        isEditMode={isEditMode}
                        initialData={isEditMode ? project?.milestones || [] : []}
                        itemsState={milestonesState}
                        setItemsState={setMilestonesState}
                    />
                    <ProjectMilestoneReport
                        type='Reports'
                        isEditMode={isEditMode}
                        initialData={isEditMode ? project?.reports || [] : []}
                        itemsState={reportsState}
                        setItemsState={setReportsState}
                    />

                    <ProjectContacts contacts={contacts} setContacts={setContacts} />

                    {renderReach()}
                    {renderNeedsAndCapabilities()}

                    <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                        <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                            <div className='flex justify-end py-4'>
                                <div
                                    className='text-theme1-primary font-bold'
                                    onClick={() => closeNeedsAndCapabilities()}
                                >
                                    Close
                                </div>
                            </div>
                            <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>
                                {needsOrCapabilities === 'needs' ? 'Needs' : 'Capabilities'}
                            </div>
                            {needsOrCapabilities === 'needs' && (
                                <div>
                                    <div className='mb-4'>
                                        <div className='card flex justify-content-center'>
                                            <BaseTreeSelect
                                                name='needs'
                                                label='Select Needs'
                                                inputClassName='md:w-20rem w-full'
                                                value={selectedNeeds}
                                                setValue={setSelectedNeeds}
                                                options={skills}
                                                selectionMode='checkbox'
                                                display='chip'
                                                placeholder='Choose needs'
                                                showClear={false}
                                                clearErrors={clearErrors}
                                                filter={true}
                                                filterMode={'strict'}
                                                filterDelay={10}
                                                onSelect={(value) =>
                                                    addTreeSelection(
                                                        skills,
                                                        'needs',
                                                        value,
                                                        'need_id',
                                                        appendNeed,
                                                        removeNeed,
                                                        fieldsNeeds,
                                                        setSelectedNeeds
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center flex-wrap overflow-y-scroll'>
                                        <>
                                            {fieldsNeeds.length > 0 &&
                                                fieldsNeeds.map((need, index) => {
                                                    return (
                                                        <span
                                                            key={need.id}
                                                            className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                        >
                                                            <span>{need.name}</span>{' '}
                                                            <span
                                                                onClick={() =>
                                                                    removeAttribute(
                                                                        skills,
                                                                        index,
                                                                        need.need_id,
                                                                        setSelectedNeeds,
                                                                        'need_id',
                                                                        fieldsNeeds,
                                                                        removeNeed
                                                                    )
                                                                }
                                                            >
                                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                            </span>
                                                        </span>
                                                    );
                                                })}
                                        </>
                                    </div>
                                </div>
                            )}
                            {needsOrCapabilities === 'capabilities' && (
                                <div>
                                    <div className='mb-4'>
                                        <div className='card flex justify-content-center'>
                                            <BaseTreeSelect
                                                name='capabilities'
                                                label='Select Capabilities'
                                                inputClassName='md:w-20rem w-full'
                                                value={selectedCapabilities}
                                                setValue={setSelectedCapabilities}
                                                options={skills}
                                                selectionMode='checkbox'
                                                display='chip'
                                                placeholder='Choose capabilities'
                                                showClear={false}
                                                clearErrors={clearErrors}
                                                filter={true}
                                                filterMode={'strict'}
                                                filterDelay={10}
                                                onSelect={(value) =>
                                                    addTreeSelection(
                                                        skills,
                                                        'capabilities',
                                                        value,
                                                        'capability_id',
                                                        appendCapability,
                                                        removeCapability,
                                                        fieldsCapabilities,
                                                        setSelectedCapabilities
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className='flex items-center flex-wrap overflow-y-scroll'>
                                        <>
                                            {fieldsCapabilities.length > 0 &&
                                                fieldsCapabilities.map((capability, index) => {
                                                    return (
                                                        <span
                                                            key={capability.id}
                                                            className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                                        >
                                                            <span>{capability.name}</span>{' '}
                                                            <span
                                                                onClick={() =>
                                                                    removeAttribute(
                                                                        skills,
                                                                        index,
                                                                        capability.capability_id,
                                                                        setSelectedCapabilities,
                                                                        'capability_id',
                                                                        fieldsCapabilities,
                                                                        removeCapability
                                                                    )
                                                                }
                                                            >
                                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                                            </span>
                                                        </span>
                                                    );
                                                })}
                                        </>
                                    </div>
                                </div>
                            )}
                        </div>
                    </BaseOverlay>

                    {!!userPermissions[PERMISSION_FILES]?.read && (
                        <AssociateFiles
                            fields={fieldsFiles}
                            append={appendFile}
                            remove={removeFile}
                            showSearch={showFileSearch}
                            setShowSearch={setShowFileSearch}
                        />
                    )}

                    <BaseOverlay show={showReachOverlay} className='overflow-auto h-[100dvh] rounded-none'>
                        <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                            <div className='flex justify-end py-4'>
                                <div
                                    className='text-theme1-primary font-bold'
                                    onClick={() => setShowReachOverlay(false)}
                                >
                                    Close
                                </div>
                            </div>
                            <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>Reach</div>
                            <div>
                                <div className='mb-4'>
                                    <div className='card flex justify-content-center'>
                                        <MultiSelect
                                            value={selectedEconomicRegions}
                                            options={economicRegions}
                                            optionGroupTemplate={groupHeaderTemplate}
                                            onChange={(e) => selectReach(e.value)}
                                            optionLabel='label'
                                            optionGroupLabel='label'
                                            optionGroupChildren='items'
                                            placeholder='Select economic regions'
                                            className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 outline-none px-2 files-multi-select'
                                            showSelectAll={false}
                                            filter
                                        />
                                    </div>
                                </div>
                                <div className='flex items-center flex-wrap overflow-y-scroll'>
                                    {fieldsReach.map((region, index) => (
                                        <span
                                            key={region.id}
                                            className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                        >
                                            <span>{region.name}</span>
                                            <span
                                                onClick={() => {
                                                    selectReach(
                                                        fieldsReach
                                                            .filter(
                                                                (item) =>
                                                                    item.economic_region_id !==
                                                                    region.economic_region_id
                                                            )
                                                            .map((item) => item.economic_region_id)
                                                    );
                                                }}
                                            >
                                                <IconClose className='ml-2 w-5 h-5 fill-theme1-white' />
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </BaseOverlay>

                    <button
                        type='submit'
                        className='p-4 rounded-4xl text-lg font-bold leading-4.5 w-full btn-primary mt-8'
                        disabled={isAdding || isEditing}
                    >
                        {isEditMode ? 'Update Project' : 'Add Project'}
                    </button>
                </form>
                {!!userPermissions[PERMISSION_PROJECTS]?.delete &&
                    isEditMode &&
                    project &&
                    canUpdateOrDelete(user, project, PERMISSION_PROJECTS, 'delete') && (
                        <BaseButton
                            className='btn-primary-outline mt-4'
                            textValue='Delete project'
                            onClick={handleDeleteConfirmation}
                        />
                    )}
            </div>
            {deleteVisible && (
                <ProjectDeleteConfirmation
                    deleteVisible={deleteVisible}
                    setDeleteVisible={setDeleteVisible}
                    project={project}
                />
            )}
        </AuthLayout>
    );
};

export default AddEditProject;
